import { useMemo, useRef, useState } from "react"
import { Meeting, TranscriptEntry, TranscriptSpeaker } from "@/schema"
import { Input, ScrollArea, Separator } from "@ui/shad"
import { Pencil } from "lucide-react"

import { useRenameTranscriptSpeaker } from "@/hooks/api/meetings/mutations"

interface SpeakerStats {
  id: string | number
  name: string
  isHost: boolean
  totalSpeakingTime: number
  percentage: number
  segments: Array<{
    start: number
    end: number
  }>
}

function SpeakerName({
  speaker,
  transcriptSpeaker,
  meetingId,
}: {
  speaker: SpeakerStats
  transcriptSpeaker?: TranscriptSpeaker
  meetingId: string
}) {
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState("")
  const inputRef = useRef<HTMLInputElement>(null)
  const cancelledRef = useRef(false)
  const renameMutation = useRenameTranscriptSpeaker()

  function handleStartEdit() {
    if (!transcriptSpeaker) return
    cancelledRef.current = false
    setEditValue(transcriptSpeaker.effective_name)
    setIsEditing(true)
    setTimeout(() => inputRef.current?.select(), 0)
  }

  function handleSave() {
    if (!transcriptSpeaker || cancelledRef.current) {
      setIsEditing(false)
      return
    }
    setIsEditing(false)

    const trimmed = editValue.trim()
    if (!trimmed || trimmed === transcriptSpeaker.effective_name) return

    const overriddenName = trimmed === transcriptSpeaker.original_name ? null : trimmed
    renameMutation.mutate({
      meetingId,
      speakerId: transcriptSpeaker.id,
      overriddenName,
    })
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter") {
      handleSave()
    } else if (e.key === "Escape") {
      cancelledRef.current = true
      setIsEditing(false)
    }
  }

  if (isEditing) {
    return (
      <Input
        ref={inputRef}
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        className="-ml-1.5 h-6 w-40 px-[5px] py-0 text-base! font-medium"
        autoFocus
      />
    )
  }

  return (
    <button
      onClick={handleStartEdit}
      className="group flex items-center gap-1.5"
      disabled={!transcriptSpeaker}
      title={transcriptSpeaker ? "Click to rename" : undefined}
    >
      <span className="font-medium">{speaker.name}</span>
      {transcriptSpeaker && <Pencil className="text-muted-foreground hidden size-3 group-hover:block" />}
    </button>
  )
}

export default function MeetingSpeakersList({ meeting }: { meeting?: Meeting }) {
  const speakerNameMap = useMemo(() => {
    const map = new Map<string | number, string>()
    meeting?.transcript_speakers?.forEach((s) => map.set(String(s.provider_id), s.effective_name))
    return map
  }, [meeting?.transcript_speakers])

  const speakerStats = useMemo(() => {
    if (!meeting?.transcript || !Array.isArray(meeting.transcript) || meeting.transcript.length === 0) {
      return { speakers: [], totalDuration: 0, timeline: [] }
    }

    const transcript = meeting.transcript

    // Find the total duration from the last timestamp
    const lastEntry = transcript[transcript.length - 1]
    const totalDuration = lastEntry.words[lastEntry.words.length - 1].end_timestamp.relative

    // Create timeline segments with speaker info
    interface TimelineSegment {
      speakerId: string | number
      speakerName: string
      start: number
      end: number
    }

    // First, collect all segments
    const allSegments: TimelineSegment[] = []
    transcript.forEach((entry: TranscriptEntry) => {
      allSegments.push({
        speakerId: entry.participant.id,
        speakerName: speakerNameMap.get(String(entry.participant.id)) || entry.participant.name,
        start: entry.words[0].start_timestamp.relative,
        end: entry.words[entry.words.length - 1].end_timestamp.relative,
      })
    })

    // Sort by start time
    allSegments.sort((a, b) => a.start - b.start)

    // Create non-overlapping timeline by extending segments to fill gaps
    const timeline: TimelineSegment[] = []

    for (let i = 0; i < allSegments.length; i++) {
      const segment = allSegments[i]
      let segmentStart = segment.start
      let segmentEnd = segment.end

      // If this is not the first segment and there's a gap, extend previous segment
      if (timeline.length > 0) {
        const lastSegment = timeline[timeline.length - 1]
        if (segmentStart > lastSegment.end) {
          // Extend the previous segment to fill the gap
          lastSegment.end = segmentStart
        }
        // If this segment overlaps with previous, adjust start
        if (segmentStart < lastSegment.end) {
          segmentStart = lastSegment.end
        }
      }

      // Find the next segment from a different speaker
      let nextDifferentSpeakerStart = totalDuration
      for (let j = i + 1; j < allSegments.length; j++) {
        if (allSegments[j].speakerId !== segment.speakerId && allSegments[j].start < segmentEnd) {
          nextDifferentSpeakerStart = allSegments[j].start
          break
        }
      }

      // Extend this segment until the next different speaker starts
      segmentEnd = Math.min(segmentEnd, nextDifferentSpeakerStart)

      // If this is the last segment or the last segment from this speaker before another speaker,
      // potentially extend it further
      if (i === allSegments.length - 1 || nextDifferentSpeakerStart < totalDuration) {
        // Check if we should extend to fill silence
        let shouldExtend = true
        for (let j = i + 1; j < allSegments.length; j++) {
          if (allSegments[j].start <= segmentEnd) {
            shouldExtend = false
            break
          }
        }
        if (shouldExtend && i < allSegments.length - 1) {
          segmentEnd = allSegments[i + 1].start
        } else if (shouldExtend && i === allSegments.length - 1) {
          segmentEnd = totalDuration
        }
      }

      // Only add segment if it has positive duration
      if (segmentEnd > segmentStart) {
        timeline.push({
          speakerId: segment.speakerId,
          speakerName: segment.speakerName,
          start: segmentStart,
          end: segmentEnd,
        })
      }
    }

    // Group by speaker and calculate stats
    const speakerMap = new Map<string | number, SpeakerStats>()

    transcript.forEach((entry: TranscriptEntry) => {
      const participantId = entry.participant.id

      if (!speakerMap.has(participantId)) {
        speakerMap.set(participantId, {
          id: participantId,
          name: speakerNameMap.get(String(entry.participant.id)) || entry.participant.name,
          isHost: entry.participant.is_host,
          totalSpeakingTime: 0,
          percentage: 0,
          segments: [],
        })
      }
    })

    // Calculate speaking time from timeline
    timeline.forEach((segment) => {
      const speaker = speakerMap.get(segment.speakerId)!
      const duration = segment.end - segment.start
      speaker.totalSpeakingTime += duration
    })

    // Calculate total speaking time
    const speakers = Array.from(speakerMap.values())

    // Calculate percentages based on total meeting duration
    speakers.forEach((speaker) => {
      speaker.percentage = totalDuration > 0 ? (speaker.totalSpeakingTime / totalDuration) * 100 : 0
    })

    // Sort by speaking time (descending)
    speakers.sort((a, b) => b.totalSpeakingTime - a.totalSpeakingTime)

    return { speakers, totalDuration, timeline }
  }, [meeting, speakerNameMap])

  if (meeting?.transcript_provider === "user_upload") {
    return (
      <div className="flex h-full items-center justify-center p-12">
        <div className="text-center">
          <p className="text-muted-foreground text-sm">Speaker data is not available for uploaded transcripts.</p>
        </div>
      </div>
    )
  }

  if (!meeting?.transcript || !Array.isArray(meeting.transcript) || meeting.transcript.length === 0) {
    return null
  }

  const formatDuration = (seconds: number) => {
    const totalSeconds = Math.round(seconds)
    if (totalSeconds < 60) {
      return `${totalSeconds}s`
    }
    const minutes = Math.floor(totalSeconds / 60)
    const remainingSeconds = totalSeconds % 60
    if (remainingSeconds === 0) {
      return `${minutes}min`
    }
    return `${minutes}min ${remainingSeconds}s`
  }

  return (
    <>
      <ScrollArea className="h-full">
        <div className="space-y-6 p-6">
          {speakerStats.speakers.map((speaker, index) => {
            const transcriptSpeaker = meeting.transcript_speakers?.find(
              (s) => String(s.provider_id) === String(speaker.id)
            )

            return (
              <div key={speaker.id} className="space-y-1">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <SpeakerName speaker={speaker} transcriptSpeaker={transcriptSpeaker} meetingId={meeting.id} />
                  </div>
                  <div className="text-muted-foreground flex items-center gap-2 text-sm">
                    <span>{speaker.percentage.toFixed(1)}%</span>
                    <Separator orientation="vertical" className="h-4" />
                    <span>{formatDuration(speaker.totalSpeakingTime)}</span>
                  </div>
                </div>

                {/* Speaking timeline - show only segments for this speaker */}
                <div className="bg-tertiary relative h-2 overflow-hidden rounded-full">
                  {speakerStats.timeline
                    .filter((segment) => segment.speakerId === speaker.id)
                    .map((segment, segmentIndex) => {
                      const startPercent = (segment.start / speakerStats.totalDuration) * 100
                      const widthPercent = ((segment.end - segment.start) / speakerStats.totalDuration) * 100
                      return (
                        <div
                          key={segmentIndex}
                          className="absolute h-full"
                          style={{
                            left: `${startPercent}%`,
                            width: `${widthPercent}%`,
                            backgroundColor: `var(--chart-${(index % 5) + 1})`,
                          }}
                        />
                      )
                    })}
                </div>
              </div>
            )
          })}
        </div>
      </ScrollArea>
    </>
  )
}
